import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Label } from "@osago/ui";
import { buildWidgetAttrs, buildThemeColors } from "@/shared/lib/snippet";
import { WIDGET_CLASS } from "@/shared/config/widget";
import { WIDGET_HOST } from "@/shared/config/env";
import { useDebouncedValue } from "@/shared/lib/useDebouncedValue";
import { mountWidget, handleWidgetMessage, PREVIEW_ID } from "@embed/mountWidget";
import { useConfigurator } from "../model/configuratorContext";
import { useWidgetConfig } from "../model/useWidgetConfig";
import { SnippetOutput } from "./SnippetOutput";

const previewOrigin = new URL(WIDGET_HOST).origin;

export function PreviewPanel() {
  const { partnerId, b, chan, hidePromo, hideTitle, colors, product, companies, companiesCleared } =
    useConfigurator();
  const config = useWidgetConfig();
  const hostRef = useRef<HTMLDivElement>(null);
  const frameReady = useRef(false);
  const [loaded, setLoaded] = useState(false);

  const mountKey = useDebouncedValue(
    `${partnerId.trim()}|${b}|${chan.trim()}|${product}|${hideTitle}|${companies.join(",")}|${companiesCleared}`,
    400,
  );

  useEffect(() => {
    const listener = (e: MessageEvent) => handleWidgetMessage(WIDGET_HOST, e);
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  const theme = useMemo(
    () => ({ type: "osago-theme", colors: buildThemeColors(colors), hidePromo }),
    [colors, hidePromo],
  );
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    frameReady.current = false;
    setLoaded(false);

    if (!partnerId.trim() || companiesCleared) {
      host.replaceChildren();
      return;
    }

    const widget = document.createElement("div");
    widget.className = WIDGET_CLASS;
    for (const [name, value] of Object.entries(buildWidgetAttrs(config))) {
      widget.setAttribute(name, value);
    }
    host.replaceChildren(widget);
    mountWidget(WIDGET_HOST, widget, PREVIEW_ID);

    const iframe = widget.querySelector("iframe");
    iframe?.addEventListener("load", () => {
      frameReady.current = true;
      setLoaded(true);
      iframe.contentWindow?.postMessage(themeRef.current, previewOrigin);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountKey]);

  // Life reload widget
  useEffect(() => {
    if (!frameReady.current) return;
    const iframe = hostRef.current?.querySelector("iframe");
    iframe?.contentWindow?.postMessage(theme, previewOrigin);
  }, [theme]);

  const empty = !partnerId.trim() || companiesCleared;

  return (
    <div className="flex flex-col gap-4">
      <SnippetOutput />
      <Card padding="none" className="flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 border-b border-input-border px-4 py-2.5">
          <span
            className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${loaded ? "preview-dot-on" : "bg-input-border"
              }`}
          />
          <Label variant="caption">Предпросмотр виджета на сайте</Label>
        </div>
        <div className="relative">
          <div ref={hostRef} />
          {empty && (
            <div className="flex min-h-80 items-center justify-center p-8 text-center">
              <Label variant="body" className="max-w-xs">
                {companiesCleared
                  ? "Выберите страховые компании слева — и здесь появится живой виджет ОСАГО"
                  : "Укажите партнёрский ID слева — и здесь появится живой виджет ОСАГО"}
              </Label>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
